import { ref } from 'vue';
import api from '@/services/api';
import type { CustomerAddress } from '@/types/customer';

export const useAddressService = () => {
  const addresses = ref<CustomerAddress[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const addressService = {
    async getCustomerAddresses(customerId: number): Promise<CustomerAddress[]> {
      try {
        const response = await api.get(`/customers/addresses/customer/${customerId}`);
        return response as unknown as CustomerAddress[];
      } catch (err) {
        console.error('Error fetching addresses:', err);
        throw new Error('Failed to fetch addresses');
      }
    },

    async createAddress(customerId: number, data: any): Promise<CustomerAddress> {
      try {
        const response = await api.post('/customers/addresses/', {
          customer_id: customerId,
          ...data
        });
        return response as unknown as CustomerAddress;
      } catch (err) {
        console.error('Error creating address:', err);
        throw new Error('Failed to create address');
      }
    },

    async updateAddress(addressId: number, customerId: number, data: any): Promise<CustomerAddress> {
      try {
        const response = await api.put(`/customers/addresses/${addressId}`, {
          customer_id: customerId,
          ...data
        });
        return response as unknown as CustomerAddress;
      } catch (err) {
        console.error('Error updating address:', err);
        throw new Error('Failed to update address');
      }
    },

    async deleteAddress(addressId: number, customerId: number): Promise<void> {
      try {
        await api.delete(`/customers/addresses/${addressId}?customer_id=${customerId}`);
      } catch (err) {
        console.error('Error deleting address:', err);
        throw new Error('Failed to delete address');
      }
    },

    async setDefaultAddress(addressId: number, customerId: number): Promise<CustomerAddress> {
      try {
        const response = await api.put(`/customers/addresses/${addressId}/default`, {
          customer_id: customerId
        });
        return response as unknown as CustomerAddress;
      } catch (err) {
        console.error('Error setting default address:', err);
        throw new Error('Failed to set default address');
      }
    },
  };

  const loadAddresses = async (customerId: number) => {
    isLoading.value = true;
    error.value = null;
    try {
      addresses.value = await addressService.getCustomerAddresses(customerId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load addresses';
      console.error('Error loading addresses:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const createAddress = async (customerId: number, data: any) => {
    isLoading.value = true;
    error.value = null;
    try {
      const created = await addressService.createAddress(customerId, data);
      addresses.value.push(created);
      return created;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create address';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateAddress = async (addressId: number, customerId: number, data: any) => {
    isLoading.value = true;
    error.value = null;
    try {
      const updated = await addressService.updateAddress(addressId, customerId, data);
      const index = addresses.value.findIndex(a => a.id === updated.id);
      if (index !== -1) {
        addresses.value[index] = updated;
      }
      return updated;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update address';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteAddress = async (addressId: number, customerId: number) => {
    isLoading.value = true;
    error.value = null;
    try {
      await addressService.deleteAddress(addressId, customerId);
      addresses.value = addresses.value.filter(a => a.id !== addressId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete address';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const setDefaultAddress = async (addressId: number, customerId: number) => {
    isLoading.value = true;
    error.value = null;
    try {
      const updated = await addressService.setDefaultAddress(addressId, customerId);
      addresses.value.forEach(address => {
        address.is_default = address.id === updated.id;
      });
      return updated;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to set default address';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    addresses,
    isLoading,
    error,
    loadAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  };
};
